/**
 * Responsibility: Implement world presentation behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function hashText(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function hslToHex(h: number, s: number, l: number): string {
  const hue = ((h % 360) + 360) % 360
  const sat = clamp(s, 0, 100) / 100
  const light = clamp(l, 0, 100) / 100

  const c = (1 - Math.abs(2 * light - 1)) * sat
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1))
  const m = light - c / 2

  let r = 0
  let g = 0
  let b = 0

  if (hue < 60) [r, g, b] = [c, x, 0]
  else if (hue < 120) [r, g, b] = [x, c, 0]
  else if (hue < 180) [r, g, b] = [0, c, x]
  else if (hue < 240) [r, g, b] = [0, x, c]
  else if (hue < 300) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]

  const toHex = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

function keywordHue(text: string): number | null {
  const src = String(text || '').toLowerCase()
  if (/\b(forest|grove|vine|marsh|swamp|moss|jungle|garden)\b/.test(src)) return 138
  if (/\b(blood|war|ember|ash|lava|forge|hell|scarlet|crimson)\b/.test(src)) return 6
  if (/\b(ocean|sea|coast|storm|harbor|river|tide|rain)\b/.test(src)) return 196
  if (/\b(arcane|astral|dream|witch|rune|mage|crystal|veil)\b/.test(src)) return 272
  if (/\b(desert|sun|dune|dust|amber|brass|clockwork)\b/.test(src)) return 32
  if (/\b(void|abyss|shadow|crypt|midnight|eclipse|obsidian)\b/.test(src)) return 232
  return null
}

function deriveHue(worldSeed: string, roomText: string): number {
  const preferred = keywordHue(`${worldSeed} ${roomText}`)
  if (preferred != null) return preferred
  return hashText(worldSeed || roomText || 'adventure-machine') % 360
}

function deriveVariantName(hue: number): string {
  if (hue < 24 || hue >= 336) return 'ember'
  if (hue < 70) return 'gilded'
  if (hue < 150) return 'verdant'
  if (hue < 210) return 'tide'
  if (hue < 285) return 'arcane'
  return 'twilight'
}

import { WorldColorScheme, type WorldPresentation, type WorldTaxonomy } from "@anyquest/shared-core"

export function deriveWorldPresentation(
  worldSeed: string, 
  roomText = '', 
  taxonomy?: WorldTaxonomy
): WorldPresentation {
  const worldHash = hashText(worldSeed || 'adventure-machine')
  const roomHash = hashText(roomText || 'room')
  const baseHue = deriveHue(worldSeed, roomText)
  const sceneShift = ((roomHash % 29) - 14) * 0.9
  const hue = (baseHue + sceneShift + 360) % 360

  let scheme: WorldColorScheme

  if (taxonomy?.colorScheme) {
    // Priority 1: Use the AI-generated scheme from genesis
    scheme = WorldColorScheme.fromAI(taxonomy.colorScheme)
  } else {
    // Priority 2: Derive from hash (legacy/fallback)
    const accent = hslToHex(hue, 88, 66)
    const accentSoft = hslToHex(hue, 76, 58)
    const bg = hslToHex(hue, 36, 5 + (worldHash % 3))
    const surface = hslToHex(hue, 34, 12 + (worldHash % 4))
    const raised = hslToHex(hue + 8, 36, 17 + (roomHash % 4))
    const card1 = hslToHex(hue + 6, 30, 14)
    const card2 = hslToHex(hue + 12, 32, 18)
    const card3 = hslToHex(hue - 6, 28, 11)
    const border = hslToHex(hue + 4, 42, 38)

    scheme = new WorldColorScheme({
      '--bg': bg,
      '--surface': surface,
      '--surface-raised': raised,
      '--surface-card-1': card1,
      '--surface-card-2': card2,
      '--surface-card-3': card3,
      '--border': border,
      '--gold': accent,
      '--gold-glow': `${accent}26`,
      '--text-sys': accentSoft,
    })
  }

  return {
    variantName: deriveVariantName(hue),
    style: scheme.toCSSVariables(),
  }
}

export function stringifyWorldPresentationStyle(style: Record<string, string>): string {
  return Object.entries(style).map(([key, value]) => `${key}:${value}`).join(';')
}
